import { Lockboxes } from "../../cluetheory/Lockboxes";
import { LockBoxReader } from "../cluereader/LockBoxReader";
import { Vector2 } from "../../../lib/math";
import { CapturedModal } from "../cluereader/capture/CapturedModal";
import { AbstractPuzzleProcess } from "./AbstractPuzzleProcess";
import { AbstractPuzzleSolving } from "./AbstractPuzzleSolving";
import { deps } from "../../dependencies";
import { Log } from "../../../lib/util/Log";
import { util } from "../../../lib/util/util";
import { Alt1Color } from "../../../lib/alt1/Alt1Color";
var log = Log.log;
var async_init = util.async_init;
import { Alt1OverlayDrawCalls } from "../../../lib/alt1/overlay/Alt1OverlayDrawCalls";
import { FakeLodash } from "../../../lib/coreutil/FakeLodash";
class LockboxSolvingProcess extends AbstractPuzzleProcess {
    constructor(parent) {
        super();
        this.parent = parent;
        this.settings = deps().app.settings.settings.solving.puzzles.lockboxes;
        this.cost_function = Lockboxes.MoveMap.scoring([0, 1, this.settings.two_click_factor]);
        this.isSolved = false;
        this.last_state = null;
        this.initialization = async_init(async () => {
            return {
                reader: await LockBoxReader.instance()
            };
        });
    }
    overlay(solution, reader, is_desynced) {
        const geometry = new Alt1OverlayDrawCalls.GeometryBuilder();
        for (let y = 0; y < solution.length; y++) {
            const row = solution[y];
            for (let x = 0; x < row.length; x++) {
                const tile = row[x];
                if (tile == 0)
                    continue;
                geometry.text(tile.toString(), Vector2.add(Vector2.scale(0.5, LockBoxReader.TILE_SIZE), reader.tileOrigin({ x, y }, true), { x: 3, y: 0 }), {
                    width: 24,
                    color: Alt1Color.fromNumber(this.settings.overlay_color)
                });
            }
        }
        if (is_desynced) {
            geometry.text("Detected Client Desync - Overlay paused", reader.tileOrigin({ x: 2, y: -2 }, true), {
                width: 16,
                color: Alt1Color.fromHex("#C80000"),
                centered: true,
                shadow: true
            });
        }
        this.solution_overlay.setGeometry(geometry.buffer());
    }
    area() {
        return this.parent.lockbox.reader.modal.body.screenRectangle();
    }
    async tick(capt) {
        if (!capt)
            return;
        if (!this.initialization.isInitialized())
            return;
        const capture = CapturedModal.assumeBody(capt);
        const reader = new LockBoxReader.CapturedLockbox(capture, this.initialization.get().reader);
        const puzzle = reader.getPuzzle();
        if (reader.getState() == "likelyclosed")
            this.puzzleClosed();
        if (puzzle) {
            if (this.last_state) {
                // If there is already a previous solution, find the most similar solution
                const is_visually_desynced = (() => {
                    const required_moves_from_last_accepted_state = Lockboxes.solve(puzzle, {
                        target_override: this.last_state.state,
                        modulo_targets_allowed: false,
                        minimize: true,
                        minimize_by: Lockboxes.MoveMap.clickScore
                    });
                    const moves_required = Lockboxes.MoveMap.clickScore(required_moves_from_last_accepted_state.moves);
                    // Assumes a maximum click rate of 20/s
                    const max_plausible_moves = Math.ceil((capt.capture.timestamp - this.last_state.timestamp) / 80) + 1;
                    return moves_required > max_plausible_moves;
                })();
                if (!is_visually_desynced) {
                    if (this.last_state.visually_desynced)
                        log().log("Visual desync ended", "Lockboxes");
                    const solution = Lockboxes.solve(puzzle, {
                        target_override: this.last_state.solution.target,
                        minimize: true,
                        modulo_targets_allowed: true,
                        minimize_by: s => Lockboxes.MoveMap.difference(s, this.last_state.solution.moves)
                    });
                    this.last_state = {
                        state: puzzle,
                        solution: solution,
                        timestamp: capt.capture.timestamp
                    };
                }
                else {
                    if (!this.last_state.visually_desynced)
                        log().log("Visual desync detected", "Lockboxes");
                    this.last_state.visually_desynced = true;
                    const DESYNC_TIMEOUT = 3000;
                    if ((capt.capture.timestamp - this.last_state.timestamp) > DESYNC_TIMEOUT) {
                        log().log("Desync timed out, Resolving", "Lockboxes");
                        this.last_state = null;
                    }
                }
            }
            // This is intentionally not an else because the previous conditional can set the last_solution back to null
            if (!this.last_state) {
                // If there is no solution, do an initial solve with the cost function
                const solution = Lockboxes.solve(puzzle, {
                    modulo_targets_allowed: true,
                    minimize: true,
                    minimize_by: this.cost_function
                });
                this.last_state = {
                    state: puzzle,
                    solution: solution,
                    timestamp: capt.capture.timestamp
                };
            }
            this.overlay(this.last_state.solution.moves, reader, this.last_state.visually_desynced);
        }
    }
    begin() {
        this.puzzle = this.parent.lockbox.reader.getPuzzle(); // This should already be cached
        super.begin();
    }
}
export class LockboxSolving extends AbstractPuzzleSolving {
    constructor(parent, lockbox) {
        super("Lockbox", parent, lockbox, deps().app.settings.settings.solving.puzzles.lockboxes.autostart, "Lockbox Puzzle", "lockboxes");
        this.lockbox = lockbox;
    }
    constructProcess() {
        return new LockboxSolvingProcess(this);
    }
    begin() {
        super.begin();
        this.modal.setImage(this.lockbox.reader.tile_area.getData());
    }
    pausesClueReader() {
        return this.process && !this.process?.isSolved;
    }
}
(function (LockboxSolving) {
    let Settings;
    (function (Settings) {
        Settings.DEFAULT = {
            autostart: true,
            two_click_factor: 1.3,
            overlay_color: Alt1Color.white.for_overlay
        };
        function normalize(settings) {
            if (!settings)
                return Settings.DEFAULT;
            if (![true, false].includes(settings.autostart))
                settings.autostart = Settings.DEFAULT.autostart;
            if (typeof settings.overlay_color != "number")
                settings.overlay_color = Settings.DEFAULT.overlay_color;
            if (typeof settings.two_click_factor != "number")
                settings.two_click_factor = Settings.DEFAULT.two_click_factor;
            settings.two_click_factor = FakeLodash.clamp(settings.two_click_factor, 1, 5);
            return settings;
        }
        Settings.normalize = normalize;
    })(Settings = LockboxSolving.Settings || (LockboxSolving.Settings = {}));
})(LockboxSolving || (LockboxSolving = {}));
